import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Plus, Calendar, ChevronDown } from "lucide-react";
import { StepProps } from "@/lib/builder/models/builder.model";
import { AccommodationCard } from "./activities-card/AccommodationCard";
import { ActivityCard } from "./activities-card/ActivityCard";
import { FlightCard } from "./activities-card/FlightCard";
import { TransportationCard } from "./activities-card/TransportationCard";
import { FoodDrinksCard } from "./activities-card/FoodDrinksCard";
import { EventsCard } from "./activities-card/EventsCard";
import { NotesCard } from "./activities-card/NotesCard";
import { useModalCallbacks } from "./hooks/useModalCallbacks";
import { ItineraryDay } from "./models/itinerary-day.model";
import {
  ActivityType,
  DayActivity,
  AccommodationActivity,
  ActivityActivity,
  FlightActivity,
  TransportationActivity,
  FoodDrinksActivity,
  EventsActivity,
  NotesActivity
} from "./models/activity.model";
import { ACTIVITIES } from "./models/activity-configs.model";
import { createCheckInOutActivities } from "@/components/utils/createCheckOutFromCheckIn";

const DayBuilderStep = ({
  data,
  onDataChange,
  canProceed,
  setCanProceed,
  selectedDate,
  isReadOnly = false
}: StepProps) => {
  const [days, setDays] = useState<ItineraryDay[]>(data?.itinerary_days || []);
  const [currentDay, setCurrentDay] = useState<ItineraryDay | null>(null);

  useEffect(() => {
    setDays(data?.itinerary_days || []);
    setCurrentDay((data?.itinerary_days || []).find(day => day.date === selectedDate) || null);
  }, [data?.itinerary_days, selectedDate]);

  // Helper function to update both local state and main data model
  const updateDays = (updatedDays: ItineraryDay[]) => {
    setDays(updatedDays);
    setCurrentDay(updatedDays.find(day => day.date === selectedDate) || null);

    onDataChange({ itinerary_days: updatedDays });
  };

  // Function to add or update activity (now accepts an array of activity data)
  const addActivities = (activityDataArray: Record<string, unknown>[], isEditing?: boolean, editingActivityId?: string) => {
    if (!selectedDate) return;

    // Make a shallow copy of days and their activities so we only mutate copies
    let updatedDays = days.map(day => ({ ...day, activities: [...day.activities] })) as ItineraryDay[];

    if (isEditing && editingActivityId && activityDataArray.length > 0) {
      const activityData = activityDataArray[0];

      // Determine target date for the new activity (use start_date if provided, otherwise selectedDate)
      const rawDate = (activityData.start_date as string) || (activityData.date as string) || selectedDate;
      const dateStr = rawDate ? rawDate.split('T')[0] : selectedDate!;

      // If editing an accommodation, remove both linked activities
      const idToDelete = [activityData.id as string];
      if (activityData.type === 'accommodation') {
        const linkedId = editingActivityId.endsWith('-in')
          ? editingActivityId.replace('-in', '-out')
          : editingActivityId.endsWith('-out')
            ? editingActivityId.replace('-out', '-in')
            : null;
        if (linkedId) idToDelete.push(linkedId);
      }

      // Remove the existing activity (wherever it is)
      updatedDays = updatedDays.map(day => ({
        ...day,
        activities: day.activities.filter(activity => !idToDelete.includes(activity.id))
      }));

      if (activityData.type === 'accommodation') {
        const checkInOut = createCheckInOutActivities(activityData as AccommodationActivity);
        addActivityIntoDays(updatedDays, checkInOut[0], checkInOut[0].start_date);
        addActivityIntoDays(updatedDays, checkInOut[1], checkInOut[1].start_date);
      } else addActivityIntoDays(updatedDays, activityData as DayActivity, dateStr);

      // ensure days are sorted by date after modification
      updatedDays.sort((a, b) => (a.date > b.date ? 1 : a.date < b.date ? -1 : 0));

      updateDays(updatedDays);
      return;
    }

    // For each provided activity data, create new DayActivity and insert into proper day
    activityDataArray.forEach(activityData => {
      // se c'è una start_date nell'activityData la usiamo, altrimenti usiamo selectedDate del giorno corrente
      const rawDate = (activityData.start_date as string) || selectedDate;
      const dateStr = rawDate ? rawDate.split('T')[0] : selectedDate!;

      const activity: DayActivity = {
        id: (activityData.id as string) || `activity-${Date.now()}-${Math.random().toString(36).substr(2, 5)}`,
        title: (activityData.title as string) || '',
        description: (activityData.description as string) || '',
        location: (activityData.location as string) || '',
        type: (activityData.type as ActivityType) || 'activity',
        date: dateStr,
        include_price: (activityData.include_price as boolean) || false,
        cost: (activityData.cost as number) || 0,
        ...activityData
      } as DayActivity;

      const dayIndex = updatedDays.findIndex(d => d.date === dateStr);
      if (dayIndex !== -1) {
        const day = updatedDays[dayIndex]; // append to existing day's activities
        updatedDays[dayIndex] = { ...day, activities: [...day.activities, activity] as DayActivity[] };
      } else {
        // ERROR missing day
      }
    });

    // ensure days are sorted by date after adding new ones
    updatedDays.sort((a, b) => (a.date > b.date ? 1 : a.date < b.date ? -1 : 0));

    // Call updateDays only once with the fully updated structure
    updateDays(updatedDays);
  };

  const addActivityIntoDays = (updatedDays: ItineraryDay[], newActivity: DayActivity, dateStr: string) => {
    // Insert the new activity into the correct day; if the day doesn't exist, create it
    const dayIndex = updatedDays.findIndex(d => d.date === dateStr);
    if (dayIndex !== -1) {
      const day = updatedDays[dayIndex];
      updatedDays[dayIndex] = { ...day, activities: [...day.activities, newActivity] as DayActivity[] };
    } else {
      // Create a new day with the activity
      const newDay: ItineraryDay = {
        date: dateStr,
        activities: [newActivity]
      } as ItineraryDay;
      updatedDays.push(newDay);
    }
  }

  // derive itinerary bounds from data.itinerary_days (take earliest and latest date)
  let itineraryStartDate: string | null = null;
  let itineraryEndDate: string | null = null;
  if (data?.itinerary_days && data.itinerary_days.length > 0) {
    const dates = data.itinerary_days
      .map((d: any) => d?.date)
      .filter(Boolean)
      .sort(); // ISO dates (YYYY-MM-DD) sort correctly as strings
    itineraryStartDate = dates[0] || null;
    itineraryEndDate = dates[dates.length - 1] || null;
  }

  const modalCallbacks = useModalCallbacks({
    onSave: addActivities,
    itineraryStartDate,
    itineraryEndDate
  });

  // Update canProceed based on whether we have at least one activity
  useEffect(() => {
    const hasActivities = days.some(day => day.activities.length > 0);
    setCanProceed(hasActivities);
  }, [days, setCanProceed]);

  const handleAddActivity = (activityType: ActivityType) => {
    modalCallbacks.openModal(activityType);
  };

  const deleteActivity = (date: string, activity: DayActivity) => {
    const idToDelete = [activity.id];
    if (activity.type === 'accommodationIn' || activity.type === 'accommodationOut') {
      // For accommodationIn or accommodationOut, delete both linked activities
      const linkedType = activity.type === 'accommodationIn' ? 'accommodationOut' : 'accommodationIn';
      const linkedId = activity.id.endsWith('-in')
        ? activity.id.replace('-in', '-out')
        : activity.id.endsWith('-out')
          ? activity.id.replace('-out', '-in')
          : null;

      if (linkedId) {
        idToDelete.push(linkedId);
      }
    }

    const updatedDays = days.map(day => ({
      ...day,
      activities: day.activities.filter(act => !idToDelete.includes(act.id))
    }));
    updateDays(updatedDays as ItineraryDay[]);
  };

  const duplicateActivity = (date: string, activity: DayActivity) => {
    const day = days.find(d => d.date === date);
    if (!day) return;

    const activityToDuplicate = day.activities.find(act => act.id === activity.id);
    if (!activityToDuplicate) return;

    const duplicatedActivity = {
      ...activityToDuplicate,
      id: `activity-${Date.now()}`,
      title: `${activityToDuplicate.title} (Copy)`
    };

    const updatedDays = days.map(day => {
      if (day.date === date) {
        const activities = [...day.activities];
        const originalIndex = activities.findIndex(act => act.id === activity.id);

        // Insert the duplicated activity right after the original
        activities.splice(originalIndex + 1, 0, duplicatedActivity);

        return { ...day, activities };
      }
      return day;
    });

    updateDays(updatedDays as ItineraryDay[]);
  };

  const formatTitle = (day: string) => {
    const date = new Date(day);
    const dayNumber = date.getDate();
    const monthName = date.toLocaleDateString('it-IT', { month: 'long' });
    return `${dayNumber} ${monthName}`;
  };

  const formatDescription = (day: string) => {
    const date = new Date(day);
    const dayName = date.toLocaleDateString('it-IT', { weekday: 'long' });
    return `${dayName}`;
  };

  // Function to render the correct card based on activity type
  const renderActivityCard = (activity: DayActivity) => {
    const commonProps = {
      onEdit: (activity: DayActivity) => {
        modalCallbacks.openModal(activity.type, undefined, activity);
      },
      onDelete: (activity: DayActivity) => deleteActivity(currentDay!.date, activity),
      onDuplicate: (activity: DayActivity) => duplicateActivity(currentDay!.date, activity),
      onExpand: () => { },
      isReadOnly
    };

    switch (activity.type) {
      case 'accommodation':
        return <AccommodationCard activity={activity as AccommodationActivity} {...commonProps} />;
      case 'activity':
        return <ActivityCard activity={activity as ActivityActivity} {...commonProps} />;
      case 'flight':
        return <FlightCard activity={activity as FlightActivity} {...commonProps} />;
      case 'transportation':
        return <TransportationCard activity={activity as TransportationActivity} {...commonProps} />;
      case 'food-drinks':
        return <FoodDrinksCard activity={activity as FoodDrinksActivity} {...commonProps} />;
      case 'events':
        return <EventsCard activity={activity as EventsActivity} {...commonProps} />;
      case 'notes':
        return <NotesCard activity={activity as NotesActivity} {...commonProps} />;
      default:
        // Fallback to a generic card for unknown types
        return <ActivityCard activity={activity as ActivityActivity} {...commonProps} />;
    }
  };

  return (
    <>
      <div className="space-y-6 p-6">
        {currentDay ? (
          <>
            {/* Day Header - Outside the card */}
            <div className="flex items-center justify-between">
              <div className="flex flex-col items-start gap-1">
                <h3 className="text-sm text-gray-900">
                  {formatTitle(currentDay.date)}
                </h3>
                <h3 className="text-lg font-semibold text-gray-900 capitalize">{formatDescription(currentDay.date)}</h3>
              </div>

              {/* Add Activity Dropdown - Only show if not read-only */}
              {!isReadOnly && (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="outline" className="flex items-center gap-2">
                      <Plus className="h-4 w-4" />
                      Add Activity
                      <ChevronDown className="h-4 w-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-48">
                    {ACTIVITIES.filter(type => !type.hidden).map((type) => (
                      <DropdownMenuItem
                        key={type.id}
                        onClick={() => handleAddActivity(type.id as ActivityType)}
                        className="flex items-center gap-2 my-1"
                      >
                        {type.icon}
                        {type.label}
                      </DropdownMenuItem>
                    ))}
                  </DropdownMenuContent>
                </DropdownMenu>
              )}
            </div>

            {/* Activities List */}
            {currentDay.activities.length > 0 ? (
              <div className="space-y-4">
                {currentDay.activities.map((activity) => (
                  <div key={activity.id}>
                    {renderActivityCard(activity)}
                  </div>
                ))}
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center py-16 text-gray-500 h-[500px]">
                <div className="bg-gray-100 rounded-full p-6 mb-6">
                  <Calendar className="h-12 w-12 text-gray-400" />
                </div>
                <h3 className="text-lg font-semibold text-gray-700 mb-2">Start building your day</h3>
                <p className="text-sm text-gray-500 text-center max-w-sm mb-6">
                  Add activities, accommodations, transportation, and more to create the perfect itinerary for this day.
                </p>
                {!isReadOnly && (
                  <div className="flex flex-wrap gap-3 justify-center">
                    {ACTIVITIES.filter(type => !type.hidden).map((activity) => (
                      <button
                        key={activity.id}
                        onClick={() => handleAddActivity(activity.id as ActivityType)}
                        className="flex items-center gap-2 px-3 py-2 bg-gray-50 rounded-lg border border-gray-200 hover:bg-gray-100 hover:border-gray-300 transition-colors cursor-pointer"
                      >
                        {activity.icon}
                        <span className="text-xs text-gray-600">{activity.label}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
          </>
        ) : (
          <div className="text-center py-10 text-gray-500">
            <Calendar className="h-12 w-12 mx-auto mb-4 text-gray-300" />
            <p className="text-sm">Please set your travel dates in the previous step to start building your itinerary.</p>
          </div>
        )}

        {/* Activity Modals */}
      </div>
      {modalCallbacks.renderModal()}
    </>
  );
};

export default DayBuilderStep;
